import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronRight, FileSpreadsheet, FileText, Filter, Loader2, RefreshCw, X } from 'lucide-react';
import BaseSelectField from '../common/SelectField.js';
import SearchSubmitInput from '../common/SearchSubmitInput.js';
import {
  filterInputClass,
  filterDateInputClass,
  filterGridClassName,
  selectDropdownOpenClass,
} from '../common/uiClasses.js';
import { DashboardLayout } from '../common/page_component/index.js';
import { useAuth } from '../../context/AuthContext.js';
import reportService from '../../services/ReportService.js';
import userService from '../../services/UserService.js';
import vehicleService from '../../services/vehicleService.js';
import { locationHubService } from '../../services/locationHubService.js';
import shipmentClientService from '../../services/ShipmentClientService.js';
import externalCommissionService from '../../services/ExternalCommissionService.js';
import expenseService from '../../services/ExpenseService.js';
import GstCollectionReportView from './GstCollectionReportView.js';
import { ReportPreviewStack } from './ReportPreviewModals.js';
import contractPartyService from '../../services/contractPartyService.js';
import {
  formatHubBranchLabel,
  formatUserWithLocation,
  formatUserNameRoleHub,
  filterShipmentLocations,
  destinationTypeLabel,
  isHubDestination,
  isBranchDestination,
} from '../../utils/destinationType.js';
import {
  REPORT_DEFS,
  buildCleanParams,
  canExportExcel,
  colorMap,
  getDefaultFilters,
  getTodayIso,
  isReportVisible,
} from './reportConfig.js';
import { formatFinancialCell, isFinancialColumnLabel } from '../../utils/formatCurrency.js';

/** Same label / trigger styles as Shipment list filters */
const filterLabelClass = 'block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2';
const filterTriggerClass =
  'w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-xl cursor-pointer flex justify-between items-center bg-white text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-400';

/** Equal-width cell wrapper used by every filter field */
const FilterField = ({ children, className = '' }) => (
  <div className={`min-w-0 w-full max-w-full ${className}`}>{children}</div>
);

const SHIPMENT_ID_COLUMNS = new Set([
  'shipment id',
  'gc number',
  'gc no',
  'gc',
]);

const isShipmentIdColumn = (col) => SHIPMENT_ID_COLUMNS.has(String(col || '').trim().toLowerCase());

const ML_SHEET_ID_COLUMNS = new Set([
  'ml sheet',
  'ml sheet id',
  'dispatch id',
  'ogcl number',
  'ogcl',
]);

const isMlSheetIdColumn = (col) => ML_SHEET_ID_COLUMNS.has(String(col || '').trim().toLowerCase());

const TOTAL_LABELS = {
  booked: 'Booked',
  dispatched: 'Dispatched',
  in_transit: 'In Transit',
  received: 'Received',
  delivered: 'Delivered',
  returned: 'Returned',
  rejected: 'Rejected',
  shipments: 'Shipments',
  articles: 'Articles',
  qty: 'Qty',
  freight: 'Freight',
  total: 'Total Amt',
  paid: 'Paid',
  balance: 'Balance',
  collected: 'Collected cash',
  paid_count: 'Paid GCs',
  paid_amount: 'Paid',
  total_amount: 'Total Amount',
  to_pay_count: 'To-Pay GCs',
  to_pay_amount: 'To Pay',
  on_account_amount: 'On Account',
  other_charges: 'Other Charges',
  credit_count: 'Credit GCs',
  credit_amount: 'Credit (Rs.)',
  loading: 'Loading',
  unloading: 'Unloading',
  itcl_created: 'ITCL Created',
  tds: 'TDS',
  outstanding: 'Outstanding',
  'booking outstanding': 'Booking outstanding',
  'delivery outstanding': 'Delivery outstanding',
  'payable / receivable': 'Payable / Receivable',
  'yesterday cumulative': 'Yesterday Cumulative',
};

const formatTotalLabel = (key) =>
  TOTAL_LABELS[key] || String(key).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const formatTotalValue = (key, value, map = {}) => {
  if (key === 'outstanding' || key === 'yesterday cumulative' || String(key).endsWith('outstanding')) {
    const n = Number(value);
    if (Number.isFinite(n)) {
      const side = key === 'outstanding'
        ? (map['payable / receivable'] || (n >= 0 ? 'Receivable' : 'Payable'))
        : (n >= 0 ? 'Receivable' : 'Payable');
      const formatted = formatFinancialCell(key, Math.abs(n));
      return `${side} ${formatted ?? Math.abs(n).toLocaleString('en-IN')}`;
    }
  }
  if (value == null || value === '') return '—';
  if (isFinancialColumnLabel(key)) {
    const formatted = formatFinancialCell(key, value);
    return formatted == null || formatted === '' ? '—' : formatted;
  }
  if (typeof value === 'number') {
    return Number(value).toLocaleString('en-IN', { maximumFractionDigits: 0 });
  }
  return String(value);
};

const TOTAL_KEY_ORDER = [
  'records', 'shipments', 'booked', 'dispatched', 'itcl_created', 'in_transit', 'received', 'delivered', 'returned', 'rejected',
  'articles', 'qty', 'freight', 'total', 'paid', 'balance', 'collected',
  'paid_count', 'paid_amount', 'total_amount', 'to_pay_count', 'to_pay_amount', 'on_account_amount', 'other_charges', 'credit_count', 'credit_amount',
  'loading', 'unloading', 'booking commission', 'delivery commission',
  'tds', 'payment received', 'yesterday cumulative', 'outstanding',
  'booking outstanding', 'delivery outstanding',
];

const HIDDEN_TOTAL_KEYS = new Set(['payable / receivable']);

const TotalsSummaryTable = ({ totals, recordCount }) => {
  if (!totals || typeof totals !== 'object') return null;
  const entries = Object.entries(totals).filter(([, v]) => v == null || ['string', 'number', 'boolean'].includes(typeof v));
  if (!entries.length && recordCount == null) return null;
  const map = Object.fromEntries(entries);
  const orderedKeys = [
    ...(recordCount != null && recordCount !== '' ? ['records'] : []),
    ...TOTAL_KEY_ORDER.filter((k) => k !== 'records' && k in map),
    ...entries.map(([k]) => k).filter((k) => !TOTAL_KEY_ORDER.includes(k) && !HIDDEN_TOTAL_KEYS.has(k)),
  ].filter((k) => !HIDDEN_TOTAL_KEYS.has(k));
  const rows = orderedKeys.map((k) => [k, k === 'records' ? recordCount : map[k]]);
  return (
    <div className="px-3 sm:px-4 py-2.5 border-b border-slate-200">
      <div className="lg:hidden grid grid-cols-2 gap-1.5">
        {rows.map(([k, v]) => (
          <div key={k} className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5 min-w-0">
            <p className="text-[10px] font-semibold text-slate-500 truncate">{formatTotalLabel(k)}</p>
            <p className="text-xs font-bold text-slate-800 truncate">{formatTotalValue(k, v, map)}</p>
          </div>
        ))}
      </div>
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full border-collapse text-[11px] sm:text-xs min-w-[320px]">
          <thead>
            <tr>
              {rows.map(([k]) => (
                <th
                  key={k}
                  className="text-left px-2 py-1.5 whitespace-nowrap font-bold border border-sky-200"
                  style={{ background: '#D6E3F8', color: '#1F4E79' }}
                >
                  {formatTotalLabel(k)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr style={{ background: '#F8FAFC' }}>
              {rows.map(([k, v]) => (
                <td key={k} className="px-2 py-1.5 border border-slate-200 whitespace-nowrap font-semibold text-slate-800">
                  {formatTotalValue(k, v, map)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AgentBreakdownTable = ({ breakdown }) => {
  const columns = breakdown?.columns || [];
  const rows = breakdown?.rows || [];
  if (!columns.length || !rows.length) return null;
  return (
    <div className="px-3 sm:px-4 py-2.5 border-b border-slate-200">
      <p className="text-[11px] font-bold text-slate-700 mb-1.5">Agents on this hub / branch</p>
      <div className="lg:hidden space-y-2">
        {rows.map((row, idx) => {
          const cells = Array.isArray(row) ? row : columns.map((c) => row[c]);
          return (
            <div key={idx} className="rounded-lg border border-slate-200 bg-white p-2">
              <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                {columns.map((col, cidx) => (
                  <div key={col} className="min-w-0">
                    <p className="text-[10px] font-semibold text-slate-400 truncate">{col}</p>
                    <p className="text-[11px] font-medium text-slate-800 break-words">
                      {cells[cidx] == null || cells[cidx] === '' ? '—' : (formatFinancialCell(col, cells[cidx]) ?? '—')}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div className="hidden lg:block overflow-x-auto">
      <table className="w-full border-collapse text-[11px] sm:text-xs min-w-[480px]">
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col}
                className="text-left px-2 py-1.5 whitespace-nowrap font-bold border border-sky-200"
                style={{ background: '#E8F0FE', color: '#1F4E79' }}
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx} style={{ background: idx % 2 ? '#F8FAFC' : '#fff' }}>
              {(Array.isArray(row) ? row : columns.map((c) => row[c])).map((cell, cidx) => (
                <td key={cidx} className="px-2 py-1.5 border border-slate-200 whitespace-nowrap">
                  {cell == null || cell === '' ? '—' : (formatFinancialCell(columns[cidx], cell) ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
};

const ReportCell = ({ col, cell, onOpenShipment, onOpenMl }) => {
  if (cell == null || cell === '') return '—';
  const text = isFinancialColumnLabel(col)
    ? String(formatFinancialCell(col, cell) ?? cell)
    : String(cell);
  const linkClass = 'text-blue-700 font-semibold underline underline-offset-2 hover:text-blue-900';
  if (isMlSheetIdColumn(col) && text && text !== '—') {
    return (
      <button type="button" onClick={() => onOpenMl?.(text.trim())} className={linkClass}>
        {text}
      </button>
    );
  }
  if (isShipmentIdColumn(col) && text && text !== '—') {
    return (
      <button type="button" onClick={() => onOpenShipment?.(text.trim())} className={linkClass}>
        {text}
      </button>
    );
  }
  return text;
};

const isStatusColumn = (col) => /status/i.test(String(col || ''));

const ReportRecordCard = ({ columns, cells, onOpenShipment, onOpenMl }) => {
  const statusIdx = columns.findIndex(isStatusColumn);
  const primaryIdx = 0;
  const detailCols = columns
    .map((col, idx) => ({ col, idx }))
    .filter(({ idx }) => idx !== primaryIdx && idx !== statusIdx);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-2.5">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-semibold text-slate-400 truncate">{columns[primaryIdx]}</p>
          <div className="text-sm font-bold text-slate-800 break-all leading-snug">
            <ReportCell col={columns[primaryIdx]} cell={cells[primaryIdx]} onOpenShipment={onOpenShipment} onOpenMl={onOpenMl} />
          </div>
        </div>
        {statusIdx >= 0 && (
          <span className="shrink-0 max-w-[42%] rounded-full bg-slate-100 border border-slate-200 px-2 py-0.5 text-[10px] font-semibold text-slate-700 text-right leading-snug">
            <ReportCell col={columns[statusIdx]} cell={cells[statusIdx]} onOpenShipment={onOpenShipment} onOpenMl={onOpenMl} />
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
        {detailCols.map(({ col, idx }) => (
          <div key={`${col}-${idx}`} className="min-w-0">
            <p className="text-[10px] font-semibold text-slate-400 truncate">{col}</p>
            <div className="text-[11px] font-medium text-slate-800 break-words leading-snug">
              <ReportCell col={col} cell={cells[idx]} onOpenShipment={onOpenShipment} onOpenMl={onOpenMl} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ReportSelect = ({ value, onChange, placeholder, options = [], label }) => (
  <FilterField>
    <BaseSelectField
      variant="filter"
      label={label}
      labelClassName={filterLabelClass}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      emptyLabel={placeholder}
      options={[
        { value: '', label: placeholder || 'All' },
        ...(options || []).map((opt) => ({ value: opt.value, label: opt.label })),
      ]}
    />
  </FilterField>
);

const TextInput = ({ value, onChange, placeholder, label }) => (
  <FilterField>
    {label && <label className={filterLabelClass}>{label}</label>}
    <SearchSubmitInput
      value={value || ''}
      onSearch={onChange}
      placeholder={placeholder}
      ariaLabel={label}
      inputClassName={filterInputClass}
    />
  </FilterField>
);

const DateRangeRow = ({ fromValue, toValue, onFromChange, onToChange, label }) => (
  <FilterField className="col-span-2">
    {label && <label className={filterLabelClass}>{label}</label>}
    <div className="flex flex-row items-stretch gap-1.5 w-full min-w-0">
      <input
        type="date"
        value={fromValue || ''}
        onChange={(e) => onFromChange(e.target.value)}
        className={`${filterDateInputClass} flex-1 min-w-0`}
        aria-label="From date"
      />
      <input
        type="date"
        value={toValue || ''}
        onChange={(e) => onToChange(e.target.value)}
        className={`${filterDateInputClass} flex-1 min-w-0`}
        aria-label="To date"
      />
    </div>
  </FilterField>
);

const CheckboxDropdown = ({ label, options, selected = [], onChange, emptyLabel, locked = false }) => {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);
  const count = selected.length;

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const selectedLabels = (options || [])
    .filter((o) => selected.includes(o.value))
    .map((o) => o.label);

  return (
    <FilterField>
      <div ref={ref} className={`relative w-full ${open ? 'z-[80]' : ''}`}>
        <label className={filterLabelClass}>{label}</label>
        <div
          role="button"
          tabIndex={locked ? -1 : 0}
          onClick={() => {
            if (locked) return;
            setOpen((v) => !v);
          }}
          onKeyDown={(e) => {
            if (locked) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setOpen((v) => !v);
            }
          }}
          className={`${filterTriggerClass} ${open ? selectDropdownOpenClass : ''} ${locked ? 'bg-slate-50 cursor-not-allowed' : ''}`}
        >
          <span className={`min-w-0 flex-1 truncate pr-2 ${count ? 'text-gray-800' : 'text-gray-400'}`}>
            {count ? selectedLabels.join(', ') : (emptyLabel || `All ${label}`)}
          </span>
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${open ? 'rotate-180 text-blue-500' : ''}`}
            aria-hidden="true"
          />
        </div>
        {open && !locked && (
          <div
            className="absolute z-50 top-full left-0 w-full bg-white border border-gray-300 rounded-xl shadow-lg mt-1"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="max-h-48 overflow-y-auto">
              {count > 0 && (
                <div
                  onClick={() => onChange([])}
                  className="px-3 py-2 text-xs text-blue-600 font-semibold hover:bg-blue-50 cursor-pointer border-b border-gray-100"
                >
                  Clear selection
                </div>
              )}
              {(options || []).map(({ value, label: optLabel }) => {
                const checked = selected.includes(value);
                return (
                  <label
                    key={value}
                    className="flex items-center gap-2 px-3 sm:px-4 py-2 hover:bg-blue-50 cursor-pointer text-sm sm:text-base text-gray-800"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        onChange(
                          checked
                            ? selected.filter((x) => x !== value)
                            : [...selected, value],
                        );
                      }}
                      className="accent-blue-500"
                    />
                    {optLabel}
                  </label>
                );
              })}
              {(options || []).length === 0 && (
                <div className="px-3 py-2 text-sm text-gray-400">No options</div>
              )}
            </div>
          </div>
        )}
      </div>
    </FilterField>
  );
};

/** Per-hub mode: none | all (hub+branches) | hub-only | partial (named branches). */
const modesFromHubFilters = (groups, hubIds, childIds, includeChildren) => {
  const hubSet = new Set((hubIds || []).map(String).filter(Boolean));
  const childSet = new Set((childIds || []).map(String).filter(Boolean));
  return (groups || []).map((g) => {
    const id = String(g.value);
    const kids = (g.children || []).map((c) => String(c.value));
    const parentIn = hubSet.has(id);
    const kidsInHub = kids.filter((k) => hubSet.has(k));
    const kidsInChild = kids.filter((k) => childSet.has(k));
    const selectedChildren = [...new Set([...kidsInChild, ...kidsInHub])];
    if (!kids.length) {
      return { id, mode: parentIn ? 'all' : 'none', selectedChildren: [] };
    }
    if (
      parentIn
      && includeChildren === false
      && !selectedChildren.length
    ) {
      return { id, mode: 'hub-only', selectedChildren: [] };
    }
    if (parentIn && includeChildren !== false && childSet.size === 0 && !kidsInHub.length) {
      return { id, mode: 'all', selectedChildren: kids };
    }
    if (selectedChildren.length) {
      if (
        selectedChildren.length === kids.length
        && parentIn
        && includeChildren !== false
        && childSet.size === 0
      ) {
        return { id, mode: 'all', selectedChildren: kids };
      }
      return { id, mode: 'partial', selectedChildren };
    }
    if (parentIn) return { id, mode: 'hub-only', selectedChildren: [] };
    return { id, mode: 'none', selectedChildren: [] };
  });
};

const filtersFromHubModes = (groups, modes) => {
  const byId = Object.fromEntries((modes || []).map((m) => [String(m.id), m]));
  const active = [];
  (groups || []).forEach((g) => {
    const m = byId[String(g.value)];
    if (!m || m.mode === 'none') return;
    const hasKids = (g.children || []).length > 0;
    active.push({
      id: String(g.value),
      mode: hasKids ? m.mode : 'leaf',
      selectedChildren: m.selectedChildren || [],
      childIds: (g.children || []).map((c) => String(c.value)),
    });
  });
  if (!active.length) {
    return { location_hub_ids: [], include_child_branches: true, child_location_hub_ids: [] };
  }
  const nonLeaf = active.filter((a) => a.mode !== 'leaf');
  const ids = active.map((a) => a.id);
  if (!nonLeaf.length || nonLeaf.every((a) => a.mode === 'all')) {
    return { location_hub_ids: ids, include_child_branches: true, child_location_hub_ids: [] };
  }
  if (nonLeaf.every((a) => a.mode === 'hub-only')) {
    return { location_hub_ids: ids, include_child_branches: false, child_location_hub_ids: [] };
  }
  if (nonLeaf.every((a) => a.mode === 'partial')) {
    return {
      location_hub_ids: ids,
      include_child_branches: true,
      child_location_hub_ids: active.flatMap((a) => a.selectedChildren),
    };
  }
  const location_hub_ids = [];
  active.forEach((a) => {
    if (a.mode === 'all' || a.mode === 'leaf') {
      location_hub_ids.push(a.id, ...a.childIds);
    } else if (a.mode === 'hub-only') {
      location_hub_ids.push(a.id);
    } else {
      location_hub_ids.push(...a.selectedChildren);
    }
  });
  return {
    location_hub_ids: [...new Set(location_hub_ids)],
    include_child_branches: false,
    child_location_hub_ids: [],
  };
};

const NestedHubDropdown = ({
  label,
  emptyLabel = 'All Hubs',
  groups = [],
  selectedHubIds = [],
  selectedChildIds = [],
  includeChildren = true,
  onChange,
  locked = false,
}) => {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  const ref = React.useRef(null);
  const parentLockedRef = React.useRef(new Set());
  const modes = modesFromHubFilters(groups, selectedHubIds, selectedChildIds, includeChildren);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setExpanded((prev) => {
      const n = new Set(prev);
      modesFromHubFilters(groups, selectedHubIds, selectedChildIds, includeChildren).forEach((m) => {
        if (m.mode !== 'none') n.add(String(m.id));
      });
      return n;
    });
    // Only when the menu opens so users can still collapse a hub.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const modeById = Object.fromEntries(modes.map((m) => [String(m.id), m]));
  const selectedLabels = [];
  groups.forEach((g) => {
    const m = modeById[String(g.value)];
    if (!m || m.mode === 'none') return;
    if (m.mode === 'all' || m.mode === 'hub-only' || !(g.children || []).length) {
      selectedLabels.push(g.label);
      return;
    }
    (g.children || []).forEach((c) => {
      if (m.selectedChildren.includes(String(c.value))) {
        selectedLabels.push(`${g.label} › ${c.label}`);
      }
    });
  });
  const count = selectedLabels.length;

  const commitModes = (nextModes) => {
    onChange(filtersFromHubModes(groups, nextModes));
  };

  const toggleParent = (group) => {
    const id = String(group.value);
    const next = modes.map((m) => ({ ...m, selectedChildren: [...(m.selectedChildren || [])] }));
    const idx = next.findIndex((m) => m.id === id);
    if (idx < 0) return;
    const kids = (group.children || []).map((c) => String(c.value));
    const cur = next[idx];
    if (cur.mode === 'all') {
      next[idx] = { id, mode: 'none', selectedChildren: [] };
      parentLockedRef.current.delete(id);
    } else if (cur.mode === 'hub-only') {
      next[idx] = { id, mode: kids.length ? 'all' : 'none', selectedChildren: kids };
      if (kids.length) parentLockedRef.current.add(id);
      else parentLockedRef.current.delete(id);
    } else {
      next[idx] = { id, mode: 'all', selectedChildren: kids };
      parentLockedRef.current.add(id);
    }
    if (kids.length && next[idx].mode !== 'none') {
      setExpanded((prev) => {
        const n = new Set(prev);
        n.add(id);
        return n;
      });
    }
    commitModes(next);
  };

  const toggleChild = (group, childId) => {
    const id = String(group.value);
    const cid = String(childId);
    const next = modes.map((m) => ({ ...m, selectedChildren: [...(m.selectedChildren || [])] }));
    const idx = next.findIndex((m) => m.id === id);
    if (idx < 0) return;
    const kids = (group.children || []).map((c) => String(c.value));
    const cur = next[idx];
    let selected = cur.mode === 'all' ? [...kids] : [...(cur.selectedChildren || [])];
    if (selected.includes(cid)) selected = selected.filter((x) => x !== cid);
    else selected = [...selected, cid];
    if (!selected.length) {
      const keepHub = parentLockedRef.current.has(id) || cur.mode === 'all' || cur.mode === 'hub-only';
      next[idx] = { id, mode: keepHub ? 'hub-only' : 'none', selectedChildren: [] };
      if (!keepHub) parentLockedRef.current.delete(id);
    } else if (selected.length === kids.length) {
      next[idx] = { id, mode: 'all', selectedChildren: kids };
      parentLockedRef.current.add(id);
    } else {
      next[idx] = { id, mode: 'partial', selectedChildren: selected };
    }
    if (kids.length) {
      setExpanded((prev) => {
        const n = new Set(prev);
        n.add(id);
        return n;
      });
    }
    commitModes(next);
  };

  return (
    <FilterField>
      <div ref={ref} className={`relative w-full ${open ? 'z-[80]' : ''}`}>
        <label className={filterLabelClass}>{label}</label>
        <div
          role="button"
          tabIndex={locked ? -1 : 0}
          onClick={() => {
            if (locked) return;
            setOpen((v) => !v);
          }}
          onKeyDown={(e) => {
            if (locked) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setOpen((v) => !v);
            }
          }}
          className={`${filterTriggerClass} ${open ? selectDropdownOpenClass : ''} ${locked ? 'bg-slate-50 cursor-not-allowed' : ''}`}
        >
          <span className={`min-w-0 flex-1 truncate pr-2 ${count ? 'text-gray-800' : 'text-gray-400'}`}>
            {count ? selectedLabels.join(', ') : emptyLabel}
          </span>
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${open ? 'rotate-180 text-blue-500' : ''}`}
            aria-hidden="true"
          />
        </div>
        {open && !locked && (
          <div
            className="absolute z-50 top-full left-0 w-full bg-white border border-gray-300 rounded-xl shadow-lg mt-1"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="max-h-72 overflow-y-auto">
              {count > 0 && (
                <div
                  onClick={() => {
                    parentLockedRef.current = new Set();
                    onChange({
                      location_hub_ids: [],
                      include_child_branches: true,
                      child_location_hub_ids: [],
                    });
                  }}
                  className="px-3 py-2 text-xs text-blue-600 font-semibold hover:bg-blue-50 cursor-pointer border-b border-gray-100"
                >
                  Clear selection
                </div>
              )}
              {groups.map((group) => {
                const m = modeById[String(group.value)] || { mode: 'none', selectedChildren: [] };
                const kids = group.children || [];
                const parentChecked = m.mode === 'all' || m.mode === 'hub-only';
                const parentIndeterminate = m.mode === 'partial';
                const isOpen = expanded.has(String(group.value));
                return (
                  <div key={group.value} className="border-b border-gray-50 last:border-b-0">
                    <div className="flex items-center gap-1 px-2 sm:px-3 py-1.5 hover:bg-blue-50 text-sm sm:text-base text-gray-800">
                      {kids.length > 0 ? (
                        <button
                          type="button"
                          aria-label={isOpen ? 'Collapse branches' : 'Expand branches'}
                          onClick={() => {
                            setExpanded((prev) => {
                              const n = new Set(prev);
                              const key = String(group.value);
                              if (n.has(key)) n.delete(key);
                              else n.add(key);
                              return n;
                            });
                          }}
                          className="p-0.5 rounded text-gray-500 hover:text-blue-600 shrink-0"
                        >
                          <ChevronRight className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                        </button>
                      ) : (
                        <span className="w-5 shrink-0" />
                      )}
                      <label className="flex items-center gap-2 min-w-0 flex-1 cursor-pointer py-0.5">
                        <input
                          type="checkbox"
                          checked={parentChecked}
                          ref={(el) => {
                            if (el) el.indeterminate = parentIndeterminate;
                          }}
                          onChange={() => toggleParent(group)}
                          className="accent-blue-500 shrink-0"
                        />
                        <span className="truncate">{group.label}</span>
                      </label>
                    </div>
                    {kids.length > 0 && isOpen && kids.map((child) => {
                      const checked = m.mode === 'all' || (m.selectedChildren || []).includes(String(child.value));
                      return (
                        <label
                          key={child.value}
                          className="flex items-center gap-2 pl-10 pr-3 py-1.5 hover:bg-blue-50 cursor-pointer text-sm text-gray-700"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleChild(group, child.value)}
                            className="accent-blue-500"
                          />
                          <span className="truncate">{group.label} › {child.label}</span>
                        </label>
                      );
                    })}
                  </div>
                );
              })}
              {groups.length === 0 && (
                <div className="px-3 py-2 text-sm text-gray-400">No options</div>
              )}
            </div>
          </div>
        )}
      </div>
    </FilterField>
  );
};

const REPORT_TYPE_ALIASES = {
  dispatch: 'ml-sheet',
  ogcl: 'ml-sheet',
  ogpl: 'ml-sheet',
  'profit-loss': 'route-pl',
  pl: 'route-pl',
  delivery: 'delivery-event',
};

const ReportDetailPage = () => {
  const { type: typeParam } = useParams();
  const type = REPORT_TYPE_ALIASES[typeParam] || typeParam;
  const navigate = useNavigate();
  const { user } = useAuth();
  const def = REPORT_DEFS[type];
  const roleName = user?.role_name || user?.role;
  const isAgentUser = roleName === 'Agent';

  const [filters, setFilters] = useState(() => getDefaultFilters(def, user));
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [tableRows, setTableRows] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [error, setError] = useState('');

  const [users, setUsers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [locations, setLocations] = useState([]);
  const [clients, setClients] = useState([]);
  const [vehicleOwners, setVehicleOwners] = useState([]);
  const [externalAgents, setExternalAgents] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [contractParties, setContractParties] = useState([]);

  useEffect(() => {
    if (!user || !def || !isReportVisible(def, user)) return undefined;
    const toArray = (d) => {
      if (Array.isArray(d)) return d;
      if (!d) return [];
      for (const key of ['users', 'vehicles', 'hubs', 'location_hubs', 'clients', 'data', 'results', 'items']) {
        if (Array.isArray(d[key])) return d[key];
        if (d.data && Array.isArray(d.data[key])) return d.data[key];
      }
      if (Array.isArray(d.data)) return d.data;
      return [];
    };
    userService.getAll(null, null, { all: true }).then((d) => setUsers(toArray(d))).catch(() => setUsers([]));
    vehicleService.getAllVehicles(null, null, { all: true }).then((d) => setVehicles(toArray(d))).catch(() => setVehicles([]));
    locationHubService.getAllHubs(null, null, { all: true })
      .then((d) => setLocations(toArray(d?.data ?? d)))
      .catch(() => setLocations([]));
    shipmentClientService.getAll(null, null, { all: true }).then((d) => setClients(toArray(d))).catch(() => setClients([]));
    if (type === 'expense' || type === 'approved-transactions') {
      expenseService.getCategories()
        .then((res) => {
          const list = toArray(res?.data ?? res);
          setExpenseCategories(
            list.map((c) => ({
              value: c.value || c.id || c,
              label: c.label || String(c.value || c).replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase()),
            })),
          );
        })
        .catch(() => setExpenseCategories([]));
    }
    if (type === 'external-commission') {
      externalCommissionService.getVehicleOwners()
        .then((res) => setVehicleOwners(toArray(res?.data?.data ?? res?.data ?? res)))
        .catch(() => setVehicleOwners([]));
      externalCommissionService.loadBulkSettlementAgents()
        .then((agents) => setExternalAgents(agents || []))
        .catch(() => setExternalAgents([]));
    }
    if (type === 'commission') {
      contractPartyService.getAllUnpaginated()
        .then((res) => {
          const list = toArray(res?.data ?? res);
          setContractParties(list);
        })
        .catch(() => setContractParties([]));
    }
    return undefined;
  }, [user, def, type]);

  const hubsById = useMemo(() => {
    const map = {};
    (locations || []).forEach((l) => {
      if (l?.id != null) {
        map[l.id] = l;
        map[String(l.id)] = l;
      }
    });
    return map;
  }, [locations]);

  const locationLabel = (l) => formatHubBranchLabel(l, hubsById) || l?.location || l?.hub_name || l?.name || '';
  const hubOptionLabel = (l) => {
    const name = locationLabel(l);
    const kind = destinationTypeLabel(l?.destination_type);
    return kind ? `${name} (${kind})` : name;
  };
  const allShipmentLocations = useMemo(
    () => filterShipmentLocations(locations || []),
    [locations]
  );
  const roleScopedLocations = useMemo(() => {
    const mineId = user?.location_hub_id != null ? String(user.location_hub_id) : '';
    if ((roleName === 'Hub Admin' || roleName === 'Agent') && mineId) {
      const mine = allShipmentLocations.find((l) => String(l.id) === mineId);
      const allowed = new Set([mineId]);
      if (mine && isHubDestination(mine.destination_type)) {
        allShipmentLocations.forEach((l) => {
          if (String(l.parent_hub_id) === mineId) allowed.add(String(l.id));
        });
      }
      return allShipmentLocations.filter((l) => allowed.has(String(l.id)));
    }
    return allShipmentLocations;
  }, [allShipmentLocations, roleName, user?.location_hub_id]);

  const hubTreeGroups = useMemo(() => {
    const parents = roleScopedLocations.filter((l) => {
      if (isHubDestination(l.destination_type) || !l.parent_hub_id) return true;
      return !roleScopedLocations.some((p) => String(p.id) === String(l.parent_hub_id));
    });
    return parents
      .map((l) => {
        const value = l.id != null ? String(l.id) : '';
        const children = roleScopedLocations
          .filter((c) => isBranchDestination(c.destination_type) && String(c.parent_hub_id) === value)
          .map((c) => ({
            value: c.id != null ? String(c.id) : '',
            label: locationLabel(c),
          }))
          .filter((o) => o.value);
        return { value, label: hubOptionLabel(l), children };
      })
      .filter((o) => o.value);
  }, [roleScopedLocations, hubsById]);

  const locationOptions = roleScopedLocations.map((l) => ({
    value: locationLabel(l) || l.location,
    label: locationLabel(l),
  }));
  const stationOptions = roleScopedLocations.map((l) => ({
    value: l.id != null ? String(l.id) : '',
    label: locationLabel(l),
  })).filter((o) => o.value);
  const flatHubOptions = stationOptions;
  const selectedHubIds = (filters.location_hub_ids || []).map(String).filter(Boolean);
  const includeChildBranches = filters.include_child_branches !== false;
  const selectedChildHubIds = (filters.child_location_hub_ids || []).map(String).filter(Boolean);
  const hubModes = useMemo(
    () => modesFromHubFilters(hubTreeGroups, selectedHubIds, selectedChildHubIds, includeChildBranches),
    [hubTreeGroups, selectedHubIds, selectedChildHubIds, includeChildBranches],
  );
  const effectiveHubIdsForAgents = useMemo(() => {
    const ids = new Set();
    const byId = Object.fromEntries(hubModes.map((m) => [String(m.id), m]));
    hubTreeGroups.forEach((g) => {
      const m = byId[String(g.value)];
      if (!m || m.mode === 'none') return;
      if (m.mode === 'hub-only') {
        ids.add(String(g.value));
        return;
      }
      if (m.mode === 'all') {
        ids.add(String(g.value));
        (g.children || []).forEach((c) => ids.add(String(c.value)));
        return;
      }
      (m.selectedChildren || []).forEach((id) => ids.add(String(id)));
    });
    if (!ids.size) selectedHubIds.forEach((id) => ids.add(id));
    return [...ids];
  }, [hubModes, hubTreeGroups, selectedHubIds]);
  const userOptions = (users || [])
    .filter((u) => u.user_id)
    .map((u) => ({
      value: String(u.user_id),
      label: formatUserWithLocation(u, hubsById),
      locationHubId: u.location_hub_id != null ? String(u.location_hub_id) : '',
    }));
  const hubScopedUserOptions = effectiveHubIdsForAgents.length
    ? userOptions.filter((u) => effectiveHubIdsForAgents.includes(u.locationHubId))
    : userOptions;
  const dailyBranchUserOptions = (users || [])
    .filter((u) => u.user_id)
    .filter((u) => {
      if (!effectiveHubIdsForAgents.length) return true;
      const hid = u.location_hub_id != null ? String(u.location_hub_id) : '';
      return effectiveHubIdsForAgents.includes(hid);
    })
    .map((u) => ({
      value: String(u.user_id),
      label: formatUserNameRoleHub(u, hubsById),
      locationHubId: u.location_hub_id != null ? String(u.location_hub_id) : '',
    }));
  const agentOptions = hubScopedUserOptions.filter((u) => {
    const raw = (users || []).find((x) => String(x.user_id) === String(u.value));
    const role = typeof raw?.role === 'string' ? raw.role : (raw?.role_name || raw?.role?.role_name || '');
    return role === 'Agent';
  });
  const contractPartyOptions = (contractParties || []).map((p) => ({
    value: p.id != null ? String(p.id) : '',
    label: p.name || p.company_name || `Party ${p.id}`,
  }));
  const vehicleOptions = (vehicles || []).map((v) => ({
    value: v.id || v.vehicle_id,
    label: `${v.vehicle_name || v.name || ''} – ${v.vehicle_number || v.number || ''}`,
  }));
  const clientOptions = (clients || []).map((c) => ({
    value: String(c.id || c.client_id || ''),
    label: c.company_name || c.name || '',
  })).filter((o) => o.value);

  const setF = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));
  const setDailyBranchUser = (userId) => {
    const match = (users || []).find((u) => String(u.user_id) === String(userId));
    const hid = match?.location_hub_id != null ? String(match.location_hub_id) : '';
    setFilters((prev) => ({
      ...prev,
      user_id: userId || '',
      ...(hid
        ? {
            location_hub_ids: [hid],
            child_location_hub_ids: [],
            include_child_branches: true,
          }
        : {}),
    }));
  };
  const applyHubScope = (scope) => {
    setFilters((prev) => {
      const next = {
        ...prev,
        location_hub_ids: scope.location_hub_ids || [],
        child_location_hub_ids: scope.child_location_hub_ids || [],
        include_child_branches: scope.include_child_branches !== false,
      };
      const effective = new Set();
      const nextModes = modesFromHubFilters(
        hubTreeGroups,
        next.location_hub_ids,
        next.child_location_hub_ids,
        next.include_child_branches,
      );
      const byId = Object.fromEntries(nextModes.map((m) => [String(m.id), m]));
      hubTreeGroups.forEach((g) => {
        const m = byId[String(g.value)];
        if (!m || m.mode === 'none') return;
        if (m.mode === 'hub-only') effective.add(String(g.value));
        else if (m.mode === 'all') {
          effective.add(String(g.value));
          (g.children || []).forEach((c) => effective.add(String(c.value)));
        } else (m.selectedChildren || []).forEach((id) => effective.add(String(id)));
      });
      if (effective.size && prev.user_id) {
        const match = (users || []).find((u) => String(u.user_id) === String(prev.user_id));
        if (!match || !effective.has(String(match.location_hub_id || ''))) next.user_id = '';
      }
      if (effective.size && prev.agent_user_id) {
        const match = (users || []).find((u) => String(u.user_id) === String(prev.agent_user_id));
        if (!match || !effective.has(String(match.location_hub_id || ''))) next.agent_user_id = '';
      }
      return next;
    });
  };
  const isHubLocked = type === 'daily-branch' && isAgentUser && Boolean(user?.location_hub_id);

  const hubScopeFilter = (label = 'Location Hub') => (
    <CheckboxDropdown
      label={label}
      emptyLabel="All locations"
      options={flatHubOptions}
      selected={selectedHubIds}
      locked={isHubLocked}
      onChange={(ids) => applyHubScope({
        location_hub_ids: ids,
        include_child_branches: false,
        child_location_hub_ids: [],
      })}
    />
  );
  const [previewStack, setPreviewStack] = useState([]);
  const [openGroups, setOpenGroups] = useState({});
  const openShipment = (shipmentId) => {
    const id = String(shipmentId || '').trim();
    if (!id) return;
    setPreviewStack((prev) => [...prev, { type: 'shipment', id }]);
  };
  const openMl = (mlSheetId) => {
    const id = String(mlSheetId || '').trim();
    if (!id) return;
    setPreviewStack((prev) => [...prev, { type: 'ml', id }]);
  };
  const closePreview = (index) => setPreviewStack((prev) => prev.slice(0, index));

  const requestSeq = React.useRef(0);
  const pageRef = useRef(1);
  const hasMoreRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const rowsLoadingRef = useRef(false);
  const sentinelRef = useRef(null);
  const PAGE_SIZE = 50;
  const pagedList = def?.layout !== 'statement';

  const loadData = useCallback(async (filterOverride) => {
    if (!def) return null;
    const activeFilters = filterOverride || filters;
    const params = buildCleanParams(activeFilters);
    if ((def.type === 'daily-branch' || def.type === 'receivable') && !params.report_date) {
      params.report_date = getTodayIso();
    }
    if (def.type === 'daily-business') {
      const today = getTodayIso();
      if (!params.from_date) params.from_date = today;
      if (!params.to_date) params.to_date = today;
    }
    const seq = ++requestSeq.current;
    pageRef.current = 1;
    hasMoreRef.current = false;
    setLoading(true);
    setRowsLoading(pagedList);
    rowsLoadingRef.current = pagedList;
    setLoadingMore(false);
    loadingMoreRef.current = false;
    setTableRows([]);
    setHasMore(false);
    setError('');
    try {
      if (!pagedList) {
        const res = await reportService.getReportData(def.type, params);
        if (seq !== requestSeq.current) return null;
        if (res?.success === false) {
          setError(res.error || 'Failed to load report');
          setPayload(null);
          return null;
        }
        setPayload(res?.data || res);
        return params;
      }

      const pageRes = await reportService.getReportData(def.type, {
        ...params,
        page: 1,
        page_size: PAGE_SIZE,
      });
      if (seq !== requestSeq.current) return null;
      if (pageRes?.success === false) {
        setError(pageRes.error || 'Failed to load report');
        return null;
      }
      const data = pageRes?.data || pageRes;
      const nextRows = data?.rows || [];
      const more = Boolean(data?.meta?.pagination?.has_more);
      setPayload(data);
      setTableRows(nextRows);
      setHasMore(more);
      hasMoreRef.current = more;
      pageRef.current = 1;
      return params;
    } catch (err) {
      if (seq !== requestSeq.current) return null;
      console.error(err);
      setError(err?.response?.data?.error || err.message || 'Failed to load report');
      setPayload(null);
      setTableRows([]);
      return null;
    } finally {
      if (seq === requestSeq.current) {
        setLoading(false);
        setRowsLoading(false);
        rowsLoadingRef.current = false;
      }
    }
  }, [def, filters, pagedList]);

  const loadMore = useCallback(async () => {
    if (!def || !pagedList) return;
    if (!hasMoreRef.current || loadingMoreRef.current || rowsLoadingRef.current) return;
    const seq = requestSeq.current;
    const nextPage = pageRef.current + 1;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const params = buildCleanParams(filters);
      if ((def.type === 'daily-branch' || def.type === 'receivable') && !params.report_date) {
        params.report_date = getTodayIso();
      }
      if (def.type === 'daily-business') {
        const today = getTodayIso();
        if (!params.from_date) params.from_date = today;
        if (!params.to_date) params.to_date = today;
      }
      const res = await reportService.getReportData(def.type, {
        ...params,
        page: nextPage,
        page_size: PAGE_SIZE,
      });
      if (seq !== requestSeq.current) return;
      if (res?.success === false) {
        setError(res.error || 'Failed to load more records');
        hasMoreRef.current = false;
        setHasMore(false);
        return;
      }
      const data = res?.data || res;
      const more = Boolean(data?.meta?.pagination?.has_more);
      pageRef.current = nextPage;
      hasMoreRef.current = more;
      setHasMore(more);
      setTableRows((prev) => [...prev, ...(data?.rows || [])]);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setError(err?.response?.data?.error || err.message || 'Failed to load more records');
    } finally {
      if (seq === requestSeq.current) {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }
    }
  }, [def, filters, pagedList]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !pagedList) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) loadMore();
    }, { rootMargin: '240px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore, pagedList, tableRows.length]);

  // Reset filters when navigating between report types
  useEffect(() => {
    if (!def || !isReportVisible(def, user)) return;
    const defaults = getDefaultFilters(def, user);
    setFilters(defaults);
    setPayload(null);
    setTableRows([]);
    setHasMore(false);
    setError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  useEffect(() => {
    if (type !== 'daily-branch') return undefined;
    if (roleName !== 'Agent' && roleName !== 'Hub Admin') return undefined;
    const mineId = user?.location_hub_id != null ? String(user.location_hub_id) : '';
    if (!mineId) return undefined;
    setFilters((prev) => {
      const current = (prev.location_hub_ids || []).map(String).filter(Boolean);
      if (roleName === 'Agent') {
        if (current.length === 1 && current[0] === mineId) return prev;
        return {
          ...prev,
          location_hub_ids: [mineId],
          include_child_branches: true,
          child_location_hub_ids: [],
        };
      }
      if (current.length) return prev;
      return {
        ...prev,
        location_hub_ids: [mineId],
        include_child_branches: true,
        child_location_hub_ids: [],
      };
    });
    return undefined;
  }, [type, roleName, user?.location_hub_id]);

  // Dynamically reload table whenever filters change (debounced)
  useEffect(() => {
    if (!def || !isReportVisible(def, user)) return undefined;
    const timer = setTimeout(() => {
      loadData(filters);
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, type, def]);

  if (!def) {
    return (
      <DashboardLayout>
        <p>Unknown report type.</p>
        <button type="button" onClick={() => navigate('/reports')}>Back</button>
      </DashboardLayout>
    );
  }

  if (!isReportVisible(def, user)) {
    return (
      <DashboardLayout>
        <p>You do not have access to this report.</p>
        <button type="button" onClick={() => navigate('/reports')}>Back</button>
      </DashboardLayout>
    );
  }

  const c = colorMap[def.color] || colorMap.indigo;
  const Icon = def.icon;
  const columns = payload?.columns || [];
  const rows = pagedList ? tableRows : (payload?.rows || []);
  const totalRecords = payload?.meta?.pagination?.total ?? payload?.meta?.count ?? rows.length;
  const totals = payload?.totals;
  const sections = payload?.sections || [];
  const isStatement = (payload?.layout === 'statement') || def.layout === 'statement' || sections.length > 0;
  const allowExcel = canExportExcel(user);

  const handleExcel = async () => {
    setExcelLoading(true);
    try {
      // Ensure export matches current filters (refresh table first if needed)
      const params = buildCleanParams(filters);
      if ((def.type === 'daily-branch' || def.type === 'receivable') && !params.report_date) {
        params.report_date = getTodayIso();
      }
      if (def.type === 'daily-business') {
        const today = getTodayIso();
        if (!params.from_date) params.from_date = today;
        if (!params.to_date) params.to_date = today;
      }
      await loadData(params);
      await reportService.downloadExcel(def.type, params);
    } catch (err) {
      alert(err?.response?.data?.error || 'Excel download failed');
    } finally {
      setExcelLoading(false);
    }
  };

  const handlePdf = async () => {
    setPdfLoading(true);
    try {
      const params = buildCleanParams(filters);
      if ((def.type === 'daily-branch' || def.type === 'receivable') && !params.report_date) {
        params.report_date = getTodayIso();
      }
      if (def.type === 'daily-business') {
        const today = getTodayIso();
        if (!params.from_date) params.from_date = today;
        if (!params.to_date) params.to_date = today;
      }
      await loadData(params);
      await reportService.downloadPdf(def.type, params);
    } catch (err) {
      alert(err?.response?.data?.error || 'PDF download failed');
    } finally {
      setPdfLoading(false);
    }
  };

  const filterFields = (() => {
    switch (type) {
      case 'user':
        return (
          <>
            <ReportSelect label="User" value={filters.user_id} onChange={(v) => setF('user_id', v)} placeholder="All Users" options={userOptions} />
            <DateRangeRow label="Date range" fromValue={filters.from_date} toValue={filters.to_date} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
          </>
        );
      case 'location':
        return (
          <>
            {hubScopeFilter('Branch')}
            <CheckboxDropdown
              label="From Station"
              emptyLabel="All Stations"
              options={stationOptions}
              selected={(filters.from_station_ids || []).map(String)}
              onChange={(v) => setF('from_station_ids', v)}
            />
            <CheckboxDropdown
              label="To Station"
              emptyLabel="All Stations"
              options={stationOptions}
              selected={(filters.to_station_ids || []).map(String)}
              onChange={(v) => setF('to_station_ids', v)}
            />
            <DateRangeRow label="Date range" fromValue={filters.from_date} toValue={filters.to_date} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
          </>
        );
      case 'vehicle':
        return (
          <>
            <ReportSelect label="Vehicle" value={filters.vehicle_id} onChange={(v) => setF('vehicle_id', v)} placeholder="All Vehicles" options={vehicleOptions} />
            <DateRangeRow label="Date range" fromValue={filters.from_date} toValue={filters.to_date} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
          </>
        );
      case 'ml-sheet':
        return (
          <>
            <TextInput label="Dispatch ID" value={filters.ml_sheet_id} onChange={(v) => setF('ml_sheet_id', v)} placeholder="Leave blank for all" />
            <DateRangeRow label="Date range" fromValue={filters.from_date} toValue={filters.to_date} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
            <CheckboxDropdown label="Sender Locations" options={locationOptions} selected={filters.sender_locations || []} onChange={(v) => setF('sender_locations', v)} />
            <CheckboxDropdown label="Receiver Locations" options={locationOptions} selected={filters.receiver_locations || []} onChange={(v) => setF('receiver_locations', v)} />
          </>
        );
      case 'shipment':
      case 'business':
        return (
          <>
            <TextInput label="Shipment ID" value={filters.shipment_id} onChange={(v) => setF('shipment_id', v)} placeholder="Leave blank for all" />
            <CheckboxDropdown
              label="Status"
              emptyLabel="All Statuses"
              options={[
                { value: 'Booked', label: 'Booked' },
                { value: 'In Transit', label: 'In Transit' },
                { value: 'Dispatched', label: 'Dispatched' },
                { value: 'Received', label: 'Received' },
                { value: 'Delivered', label: 'Delivered' },
                { value: 'Returned', label: 'Returned' },
                { value: 'Rejected', label: 'Rejected' },
              ]}
              selected={Array.isArray(filters.status) ? filters.status : (filters.status ? [filters.status] : [])}
              onChange={(v) => setF('status', v)}
            />
            <TextInput label="GST Number" value={filters.gst_number || ''} onChange={(v) => setF('gst_number', v)} placeholder="Sender or Receiver GST" />
            <TextInput label="Sender Contact" value={filters.sender_contact || ''} onChange={(v) => setF('sender_contact', v)} placeholder="Sender phone number" />
            <TextInput label="Receiver Contact" value={filters.receiver_contact || ''} onChange={(v) => setF('receiver_contact', v)} placeholder="Receiver phone number" />
            <DateRangeRow label="Date range" fromValue={filters.from_date} toValue={filters.to_date} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
            <CheckboxDropdown label="Sender Locations" options={locationOptions} selected={filters.sender_locations || []} onChange={(v) => setF('sender_locations', v)} />
            <CheckboxDropdown label="Receiver Locations" options={locationOptions} selected={filters.receiver_locations || []} onChange={(v) => setF('receiver_locations', v)} />
            {type === 'business' && (
              <>
                <CheckboxDropdown
                  label="From Station"
                  emptyLabel="All Stations"
                  options={stationOptions}
                  selected={(filters.from_station_ids || []).map(String)}
                  onChange={(v) => setF('from_station_ids', v)}
                />
                <CheckboxDropdown
                  label="To Station"
                  emptyLabel="All Stations"
                  options={stationOptions}
                  selected={(filters.to_station_ids || []).map(String)}
                  onChange={(v) => setF('to_station_ids', v)}
                />
              </>
            )}
            {hubScopeFilter()}
            <CheckboxDropdown
              label="Payment Modes"
              options={[
                { value: 'paid', label: 'Paid' },
                { value: 'cash', label: 'Cash' }, { value: 'to_pay', label: 'To Pay' },
                { value: 'credit', label: 'Credit / On Account' }, { value: 'upi', label: 'UPI' },
                { value: 'bank_transfer', label: 'Bank Transfer' }, { value: 'cheque', label: 'Cheque' },
                { value: 'online', label: 'Online' },
              ]}
              selected={filters.payment_modes || []}
              onChange={(v) => setF('payment_modes', v)}
            />
            <CheckboxDropdown
              label="Shipment Client"
              emptyLabel="All Clients"
              options={clientOptions}
              selected={(filters.shipment_client_ids || []).map(String)}
              onChange={(v) => setF('shipment_client_ids', v)}
            />
            {!isAgentUser && (
              <ReportSelect label="Agent / User" value={filters.user_id} onChange={(v) => setF('user_id', v)} placeholder="All Agents" options={hubScopedUserOptions} />
            )}
            <ReportSelect label="Vehicle" value={filters.vehicle_id} onChange={(v) => setF('vehicle_id', v)} placeholder="All Vehicles" options={vehicleOptions} />
          </>
        );
      case 'daily-business':
        return (
          <>
            {hubScopeFilter('Location Hub')}
            <CheckboxDropdown
              label="From Location"
              emptyLabel="All locations"
              options={stationOptions}
              selected={(filters.from_station_ids || []).map(String)}
              onChange={(v) => setF('from_station_ids', v)}
            />
            <CheckboxDropdown
              label="To Location"
              emptyLabel="All locations"
              options={stationOptions}
              selected={(filters.to_station_ids || []).map(String)}
              onChange={(v) => setF('to_station_ids', v)}
            />
            <DateRangeRow
              label="Date"
              fromValue={filters.from_date}
              toValue={filters.to_date}
              onFromChange={(v) => setF('from_date', v)}
              onToChange={(v) => setF('to_date', v)}
            />
            {!isAgentUser && (
              <ReportSelect
                label="User"
                value={filters.user_id || ''}
                onChange={(v) => setF('user_id', v)}
                placeholder="All users"
                options={hubScopedUserOptions}
              />
            )}
          </>
        );
      case 'receivable':
        return (
          <>
            <FilterField>
              <label className={filterLabelClass}>Report Date</label>
              <input
                type="date"
                value={filters.report_date || ''}
                onChange={(e) => setF('report_date', e.target.value || getTodayIso())}
                className={filterDateInputClass}
              />
            </FilterField>
            {hubScopeFilter()}
          </>
        );
      case 'daily-branch':
        return (
          <>
            <FilterField>
              <label className={filterLabelClass}>From date</label>
              <input
                type="date"
                value={filters.from_date || filters.report_date || ''}
                onChange={(e) => setF('from_date', e.target.value || getTodayIso())}
                className={filterDateInputClass}
              />
            </FilterField>
            <FilterField>
              <label className={filterLabelClass}>To date</label>
              <input
                type="date"
                value={filters.to_date || filters.report_date || ''}
                onChange={(e) => {
                  const value = e.target.value || getTodayIso();
                  setF('to_date', value);
                  setF('report_date', value);
                }}
                className={filterDateInputClass}
              />
            </FilterField>
            {hubScopeFilter()}
            <ReportSelect
              label="Side"
              value={filters.report_side || 'both'}
              onChange={(v) => setF('report_side', v || 'both')}
              placeholder="Both"
              options={[
                { value: 'both', label: 'Both' },
                { value: 'booking', label: 'Booking' },
                { value: 'delivery', label: 'Delivery' },
              ]}
            />
            {!isAgentUser && (
              <ReportSelect
                label="User"
                value={filters.user_id || ''}
                onChange={(v) => setDailyBranchUser(v)}
                placeholder="All Users"
                options={dailyBranchUserOptions}
              />
            )}
          </>
        );
      case 'route-pl':
        return (
          <>
            <DateRangeRow
              label="Date range"
              fromValue={filters.from_date || ''}
              toValue={filters.to_date || ''}
              onFromChange={(v) => setF('from_date', v)}
              onToChange={(v) => setF('to_date', v)}
            />
            {hubScopeFilter()}
          </>
        );
      case 'receive':
      case 'delivery-event':
      case 'ewaybills':
      case 'onaccount-credit':
      case 'invoice-cancellation':
      case 'party-booking':
        return (
          <>
            {['receive', 'delivery-event'].includes(type) && (
              <TextInput label="Shipment ID" value={filters.shipment_id || ''} onChange={(v) => setF('shipment_id', v)} placeholder="Leave blank for all" />
            )}
            {hubScopeFilter()}
            <CheckboxDropdown
              label="Shipment Client"
              emptyLabel="All Clients"
              options={clientOptions}
              selected={(filters.shipment_client_ids || []).map(String)}
              onChange={(v) => setF('shipment_client_ids', v)}
            />
            <DateRangeRow label="Date range" fromValue={filters.from_date || ''} toValue={filters.to_date || ''} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
          </>
        );
      case 'approved-transactions':
        return (
          <>
            {hubScopeFilter('Branch / Location')}
            <ReportSelect label="Category" value={filters.category || ''} onChange={(v) => setF('category', v)} placeholder="All Categories" options={expenseCategories} />
            <DateRangeRow label="Date range" fromValue={filters.from_date || ''} toValue={filters.to_date || ''} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
          </>
        );
      case 'goods-balance':
      case 'branch-payable':
        return (
          <>
            {hubScopeFilter()}
          </>
        );
      case 'hub-branch-agents':
        return (
          <>
            {hubScopeFilter('Hub / Branch')}
            <DateRangeRow label="Date range" fromValue={filters.from_date} toValue={filters.to_date} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
          </>
        );
      case 'expense':
        return (
          <>
            {hubScopeFilter('Branch / Location')}
            <ReportSelect label="Category" value={filters.category || ''} onChange={(v) => setF('category', v)} placeholder="All Categories" options={expenseCategories} />
            <DateRangeRow label="Date range" fromValue={filters.from_date} toValue={filters.to_date} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
          </>
        );
      case 'on-contract':
      case 'vehicle-contract':
      case 'trip-contract':
        return (
          <>
            <TextInput label="Contract / Trip ID" value={filters.contract_id} onChange={(v) => setF('contract_id', v)} placeholder="Leave blank for all" />
            <ReportSelect label="Vehicle" value={filters.vehicle_id} onChange={(v) => setF('vehicle_id', v)} placeholder="All Vehicles" options={vehicleOptions} />
            <DateRangeRow label="Date range" fromValue={filters.from_date} toValue={filters.to_date} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
          </>
        );
      case 'agent-tds':
        return (
          <>
            {!isAgentUser && (
              <ReportSelect label="Agent" value={filters.user_id || filters.agent_user_id} onChange={(v) => { setF('user_id', v); setF('agent_user_id', v); }} placeholder="All Agents" options={agentOptions} />
            )}
            {hubScopeFilter('Hub / Branch')}
            <DateRangeRow label="Date range" fromValue={filters.from_date} toValue={filters.to_date} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
          </>
        );
      case 'commission':
        return (
          <>
            <ReportSelect
              label="Contracted Party"
              value={filters.contract_party_id}
              onChange={(v) => setF('contract_party_id', v)}
              placeholder="All Contracted Parties"
              options={contractPartyOptions}
            />
            {!isAgentUser && (
              <ReportSelect label="Agent" value={filters.agent_user_id} onChange={(v) => setF('agent_user_id', v)} placeholder="All Agents" options={agentOptions} />
            )}
            {hubScopeFilter('Hub / Branch')}
            <ReportSelect
              label="Status"
              value={filters.status}
              onChange={(v) => setF('status', v)}
              placeholder="All Status"
              options={[{ value: 'UNPAID', label: 'Unpaid' }, { value: 'PAID', label: 'Paid' }]}
            />
            <DateRangeRow label="Date range" fromValue={filters.from_date} toValue={filters.to_date} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
          </>
        );
      case 'external-commission':
        return (
          <>
            <ReportSelect
              label="Vehicle Owner"
              value={filters.vehicle_owner_id}
              onChange={(v) => setF('vehicle_owner_id', v)}
              placeholder="All Vehicle Owners"
              options={(vehicleOwners || []).map((o) => ({
                value: o.vehicle_owner_id,
                label: o.owner_name || o.vehicle_owner_id,
              }))}
            />
            <CheckboxDropdown
              label="Booking Agents"
              options={(externalAgents.length
                ? externalAgents
                : (users || []).filter((u) => {
                  const role = typeof u.role === 'string' ? u.role : (u.role_name || u.role?.role_name || '');
                  return role === 'Agent';
                }).map((u) => ({ agent_id: u.user_id, agent_name: u.name || u.username }))
              ).map((a) => ({
                value: a.agent_id,
                label: a.agent_name || a.agent_id,
              }))}
              selected={filters.agent_user_ids || []}
              onChange={(v) => setF('agent_user_ids', v)}
            />
            <DateRangeRow label="Date range" fromValue={filters.from_date} toValue={filters.to_date} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
          </>
        );
      case 'hr':
        return (
          <>
            <ReportSelect
              label="Report Type"
              value={filters.type}
              onChange={(v) => setF('type', v)}
              placeholder="Report Type"
              options={[
                { value: 'both', label: 'Attendance + Salary' },
                { value: 'attendance', label: 'Attendance Only' },
                { value: 'salary', label: 'Salary Only' },
              ]}
            />
            {!isAgentUser && (
              <ReportSelect label="User" value={filters.user_id} onChange={(v) => setF('user_id', v)} placeholder="All Users" options={userOptions} />
            )}
            <DateRangeRow label="Date range" fromValue={filters.from_date} toValue={filters.to_date} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
            <ReportSelect
              label="Month (Salary)"
              value={filters.month}
              onChange={(v) => setF('month', v)}
              placeholder="All Months"
              options={['january','february','march','april','may','june','july','august','september','october','november','december'].map((m) => ({
                value: m, label: m.charAt(0).toUpperCase() + m.slice(1),
              }))}
            />
            <ReportSelect
              label="Salary Status"
              value={filters.status}
              onChange={(v) => setF('status', v)}
              placeholder="All Status"
              options={[{ value: 'pending', label: 'Pending' }, { value: 'credited', label: 'Credited' }]}
            />
          </>
        );
      case 'gst-collection':
        return (
          <>
            <DateRangeRow
              label="Date range"
              fromValue={filters.from_date || ''}
              toValue={filters.to_date || ''}
              onFromChange={(v) => setF('from_date', v)}
              onToChange={(v) => setF('to_date', v)}
            />
            <FilterField>
              <label className={filterLabelClass}>Type</label>
              <select
                value={filters.collection_type || 'booking'}
                onChange={(e) => setF('collection_type', e.target.value)}
                className={filterInputClass}
              >
                <option value="booking">Booking</option>
                <option value="transaction">Transaction</option>
              </select>
            </FilterField>
            <FilterField>
              <label className={filterLabelClass}>GST</label>
              <select
                value={filters.gst_filter || 'gst'}
                onChange={(e) => setF('gst_filter', e.target.value)}
                className={filterInputClass}
              >
                <option value="gst">GST</option>
                <option value="non_gst">NON GST</option>
              </select>
            </FilterField>
          </>
        );
      default:
        return (
          <DateRangeRow label="Date range" fromValue={filters.from_date || ''} toValue={filters.to_date || ''} onFromChange={(v) => setF('from_date', v)} onToChange={(v) => setF('to_date', v)} />
        );
    }
  })();

  return (
    <DashboardLayout>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>

      <div className="w-full max-w-full min-w-0">
        {/* Header: title + Excel / PDF / Back */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-3 sm:mb-4">
          <div className="flex items-start gap-2.5 sm:gap-3 min-w-0 flex-1">
            <div
              className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: c.iconBg, border: `1px solid ${c.buttonBorder}` }}
            >
              <Icon className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: c.iconColor }} />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-base sm:text-lg font-extrabold text-slate-900 m-0 leading-tight truncate">{def.title}</h1>
              <p className="text-[11px] text-slate-400 mt-0.5 m-0 leading-snug line-clamp-1 sm:line-clamp-2">{def.description}</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 shrink-0 lg:justify-end">
            {allowExcel && (
              <button
                type="button"
                onClick={handleExcel}
                disabled={excelLoading}
                className="inline-flex items-center justify-center gap-1.5 rounded-lg px-2.5 py-2 text-xs font-semibold disabled:opacity-60"
                style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#047857' }}
              >
                {excelLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5" />}
                Excel
              </button>
            )}
            <button
              type="button"
              onClick={handlePdf}
              disabled={pdfLoading}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg px-2.5 py-2 text-xs font-semibold disabled:opacity-60"
              style={{ background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1d4ed8' }}
            >
              {pdfLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
              PDF
            </button>
            <button
              type="button"
              onClick={() => navigate('/reports')}
              className="inline-flex items-center gap-1.5 shrink-0 border border-slate-200 bg-white rounded-lg px-2.5 py-2 text-xs font-semibold text-slate-600"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back
            </button>
          </div>
        </div>

        {/* Filters + Clear / Refresh */}
        <div
          className="rounded-xl border border-indigo-100 p-3 sm:p-4 mb-3 sm:mb-4"
          style={{ background: 'linear-gradient(135deg, #ffffff 0%, #eef2ff 100%)' }}
        >
          <div className="flex items-center gap-2 mb-2.5 sm:mb-3 flex-wrap">
            <Filter className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
            <span className="text-sm font-semibold text-slate-800">Filters</span>
            <span className="text-[10px] uppercase tracking-wide text-slate-400 font-medium hidden sm:inline">
              {type === 'daily-branch'
                ? 'One day + FY cumulative'
                : type === 'daily-business'
                  ? 'Defaults to today'
                  : 'Empty dates = all'}
            </span>
            <div className="ml-auto flex items-center gap-1.5 sm:gap-2">
              <button
                type="button"
                onClick={() => {
                  const defaults = getDefaultFilters(def, user);
                  setFilters(defaults);
                  loadData(defaults);
                }}
                title="Clear filters"
                className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-[11px] font-semibold border border-red-200 bg-white text-red-500 hover:bg-red-50"
              >
                <X className="w-3.5 h-3.5" strokeWidth={2.5} />
                Clear
              </button>
              <button
                type="button"
                onClick={() => loadData()}
                disabled={loading || rowsLoading}
                title="Apply / Refresh"
                className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-[11px] font-semibold disabled:opacity-60"
                style={{ background: c.buttonBg, border: `1px solid ${c.buttonBorder}`, color: c.buttonText }}
              >
                {(loading || rowsLoading)
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <RefreshCw className="w-3.5 h-3.5" strokeWidth={2.25} />}
                Refresh
              </button>
            </div>
          </div>
          <div className={`${filterGridClassName} [&>*]:min-w-0`}>
            {filterFields}
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-[10px] p-3 mb-4 text-xs sm:text-[13px]">
            {error}
          </div>
        )}

        {/* Results */}
        {type === 'gst-collection' ? (
          <>
            <p className="mb-3 text-[11px] text-amber-800">
              A date range above 7 days downloads Excel instead of listing the consignments. State-wise totals and the summary still show on this page.
            </p>
            <GstCollectionReportView payload={payload} loading={loading} />
          </>
        ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          <div className="px-3 sm:px-4 py-2.5 sm:py-3 border-b border-slate-200 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1">
            <span className="text-xs sm:text-[13px] font-bold text-slate-900 truncate">{payload?.title || def.title}</span>
            <span className="text-[11px] sm:text-xs text-slate-500">
              {loading
                ? 'Loading totals…'
                : rowsLoading
                  ? 'Loading records…'
                  : isStatement
                    ? `${sections.reduce((n, s) => n + (s.rows || []).length, 0)} line(s)`
                    : `${totalRecords.toLocaleString('en-IN')} record(s)`}
            </span>
          </div>
          {!loading && (
            <>
              <TotalsSummaryTable
                totals={totals}
                recordCount={
                  totals && Object.prototype.hasOwnProperty.call(totals, 'shipments')
                    ? undefined
                    : (isStatement
                      ? sections.reduce((n, s) => n + (s.rows || []).length, 0)
                      : totalRecords)
                }
              />
              <AgentBreakdownTable breakdown={payload?.meta?.agent_breakdown || payload?.agent_breakdown} />
            </>
          )}

          {payload?.meta?.warning && (
            <div className="mx-3 mt-3 mb-1 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              {payload.meta.warning}
            </div>
          )}

          {isStatement ? (
            <div className="p-2.5 sm:p-4 space-y-4">
              {loading && (
                <div className="text-center text-slate-400 text-sm py-8">Loading…</div>
              )}
              {!loading && sections.length === 0 && (
                <div className="text-center text-slate-400 text-sm py-8">No data for the selected filters.</div>
              )}
              {!loading && sections.map((section, sidx) => {
                const cols = section.columns || columns;
                const srows = section.rows || [];
                const rowMeta = section.row_meta || [];
                return (
                  <div key={sidx} className="rounded-xl border border-slate-200 overflow-hidden">
                    <div className="px-3 py-2 text-xs font-bold text-slate-800" style={{ background: '#B4C7E7' }}>
                      {section.title}
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse text-xs min-w-[480px]">
                        <thead>
                          <tr>
                            {cols.map((col) => (
                              <th
                                key={col}
                                className="text-left px-3 py-2 whitespace-nowrap font-bold border-b border-sky-300"
                                style={{ background: '#D6E3F8', color: '#1F4E79' }}
                              >
                                {col}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {srows.map((row, idx) => {
                            const meta = rowMeta[idx] || {};
                            const cells = Array.isArray(row) ? row : cols.map((col) => row[col]);
                            const groupName = meta.group || cells[0];
                            const groupKey = `${sidx}|${groupName}`;
                            if (meta.kind === 'agent' && !openGroups[groupKey]) return null;
                            const hasAgents = meta.kind === 'group' && rowMeta.some(
                              (item) => item?.kind === 'agent' && item.group === groupName,
                            );
                            const highlight = meta.is_nrr || section.highlight === 'total_nrr';
                            return (
                              <tr
                                key={idx}
                                style={{
                                  background: highlight
                                    ? (section.highlight === 'total_nrr' ? '#C6EFCE' : '#D9E1F2')
                                    : (meta.kind === 'agent' ? '#F8FAFC' : (idx % 2 ? '#F8FAFC' : '#fff')),
                                  fontWeight: highlight ? 700 : 400,
                                }}
                              >
                                {cells.map((cell, cidx) => (
                                  <td key={cidx} className={`px-3 py-1.5 border-b border-slate-200 whitespace-nowrap ${meta.kind === 'agent' && cidx === 0 ? 'pl-8 text-slate-600' : ''}`}>
                                    {cidx === 0 && hasAgents ? (
                                      <button
                                        type="button"
                                        className="inline-flex items-center gap-1 font-semibold text-slate-800"
                                        onClick={() => setOpenGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }))}
                                      >
                                        <ChevronRight className={`h-3.5 w-3.5 transition-transform ${openGroups[groupKey] ? 'rotate-90' : ''}`} />
                                        <ReportCell col={cols[cidx]} cell={cell} onOpenShipment={openShipment} onOpenMl={openMl} />
                                      </button>
                                    ) : (
                                      <ReportCell col={cols[cidx]} cell={cell} onOpenShipment={openShipment} onOpenMl={openMl} />
                                    )}
                                  </td>
                                ))}
                              </tr>
                            );
                          })}
                          {section.totals_row && (
                            <tr style={{ background: '#B4C7E7', fontWeight: 700 }}>
                              {(section.totals_row || []).map((cell, cidx) => (
                                <td key={cidx} className="px-3 py-2 border-b border-sky-300 whitespace-nowrap">
                                  {cell == null ? '' : (formatFinancialCell(cols[cidx], cell) ?? '')}
                                </td>
                              ))}
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <>
              {/* Phone / iPad: compact 2-field cards */}
              <div className="lg:hidden p-2.5 sm:p-3 space-y-2">
                {(loading || rowsLoading) && (
                  <div className="text-center text-slate-400 text-sm py-8 flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {loading ? 'Loading totals…' : 'Loading records…'}
                  </div>
                )}
                {!loading && !rowsLoading && rows.length === 0 && (
                  <div className="text-center text-slate-400 text-sm py-8">No data for the selected filters.</div>
                )}
                {!rowsLoading && rows.map((row, idx) => {
                  const cells = Array.isArray(row) ? row : columns.map((col) => row[col]);
                  return (
                    <ReportRecordCard
                      key={idx}
                      columns={columns}
                      cells={cells}
                      onOpenShipment={openShipment}
                      onOpenMl={openMl}
                    />
                  );
                })}
                {!loading && (payload?.totals_row || (payload?.footer_rows || []).length > 0) && (
                  <div className="rounded-lg border border-sky-200 bg-sky-50 p-2.5 space-y-2">
                    {[payload?.totals_row, ...(payload?.footer_rows || [])].filter(Boolean).map((footerRow, fidx) => (
                      <div key={`m-footer-${fidx}`} className="text-xs">
                        <p className="font-bold text-slate-800">{footerRow[0] || 'TOTAL'}</p>
                        <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-1">
                          {columns.map((col, cidx) => (
                            cidx > 0 && footerRow[cidx] !== '' && footerRow[cidx] != null ? (
                              <div key={col}>
                                <span className="text-[10px] text-slate-500">{col}: </span>
                                <span className="font-semibold">
                                  {formatFinancialCell(col, footerRow[cidx]) ?? footerRow[cidx]}
                                </span>
                              </div>
                            ) : null
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Desktop: table */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full border-collapse text-xs min-w-[640px]">
                  <thead>
                    <tr>
                      {columns.map((col) => (
                        <th
                          key={col}
                          className="text-left px-3 py-2.5 whitespace-nowrap font-bold border-b border-sky-300"
                          style={{ background: '#B4C7E7', color: '#1F4E79' }}
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(loading || rowsLoading) && (
                      <tr>
                        <td colSpan={Math.max(columns.length, 1)} className="px-6 py-8 text-center text-slate-400">
                          <span className="inline-flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            {loading ? 'Loading totals…' : 'Loading records…'}
                          </span>
                        </td>
                      </tr>
                    )}
                    {!loading && !rowsLoading && rows.length === 0 && (
                      <tr>
                        <td colSpan={Math.max(columns.length, 1)} className="px-6 py-8 text-center text-slate-400">
                          No data for the selected filters.
                        </td>
                      </tr>
                    )}
                    {!rowsLoading && rows.map((row, idx) => (
                      <tr key={idx} style={{ background: idx % 2 ? '#F2F7FF' : '#fff' }}>
                        {(Array.isArray(row) ? row : columns.map((col) => row[col])).map((cell, cidx) => (
                          <td key={cidx} className="px-3 py-2 border-b border-slate-200 whitespace-nowrap">
                            <ReportCell col={columns[cidx]} cell={cell} onOpenShipment={openShipment} onOpenMl={openMl} />
                          </td>
                        ))}
                      </tr>
                    ))}
                    {payload?.totals_row && (
                      <tr style={{ background: '#B4C7E7', fontWeight: 700 }}>
                        {(payload.totals_row || []).map((cell, cidx) => (
                          <td key={cidx} className="px-3 py-2 border-b border-sky-300 whitespace-nowrap">
                            {cell == null ? '' : (formatFinancialCell(columns[cidx], cell) ?? '')}
                          </td>
                        ))}
                      </tr>
                    )}
                    {(payload?.footer_rows || []).map((footerRow, fidx) => (
                      <tr key={`footer-${fidx}`} style={{ background: fidx === (payload.footer_rows || []).length - 1 ? '#FFF2CC' : '#B4C7E7', fontWeight: 700 }}>
                        {(footerRow || []).map((cell, cidx) => (
                          <td key={cidx} className="px-3 py-2 border-b border-sky-300 whitespace-nowrap">
                            {cell == null ? '' : (formatFinancialCell(columns[cidx], cell) ?? '')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {pagedList && (
            <div ref={sentinelRef} className="px-3 py-3 text-center text-xs text-slate-400">
              {loadingMore && (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Loading more records…
                </span>
              )}
              {!loadingMore && !rowsLoading && !loading && hasMore && 'Scroll for more records'}
              {!loadingMore && !rowsLoading && !loading && !hasMore && rows.length > 0 && 'All records loaded'}
            </div>
          )}
        </div>
        )}
      </div>
      <ReportPreviewStack
        stack={previewStack}
        onOpenShipment={openShipment}
        onOpenMl={openMl}
        onClose={closePreview}
      />
    </DashboardLayout>
  );
};

export default ReportDetailPage;
